import { Component, DestroyRef, ElementRef, inject, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  StrategyItem,
  headersTexts,
  essentialsHeadersTexts,
  optimizeCategoryName,
  KpiTargetMapping,
  StrategyHeaderTabs,
  customStrategyToStrategyItem,
} from './strategy-catalog.models';
import { StrategyCatalogService } from './services/strategy-catalog.service';
import { StrategyColumns } from '@pdqdev/pdq-shared/dist/entities/StrategyCatalog';
import { UntypedFormControl } from '@angular/forms';
import { ToastService } from '@app/storybook/services/toast/toast.service';
import { debounceTime, catchError } from 'rxjs/operators';
import { forkJoin, of } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { OPTION } from '@app/storybook/components/options-list/options-list.component';
import { PdqPopoverDirective } from '@app/storybook/components/popover/popover.directive';
import { StrategyCatalogStateService } from './services/strategy-catalog-state.service';
import { StrategyOptimizationService } from './services/strategy-optimization.service';
import { ShopService } from '@app/services/shops/shop.service';
import { UserService } from '@app/services/user/user.service';
import { MatDialog } from '@angular/material/dialog';
import { UpgradeToEnterpriseDialogService } from '@app/services/feature-access/upgrade-to-enterprise-dialog.service';
import { CreateCustomStrategyDialogComponent } from './components/create-custom-strategy-dialog/create-custom-strategy-dialog.component';
@Component({
  selector: 'strategy-catalog',
  standalone: false,
  templateUrl: './strategy-catalog.component.html',
  styleUrl: './strategy-catalog.component.scss',
})
export class StrategyCatalogComponent implements OnInit, OnDestroy {
  @ViewChild('filterPopoverRef') filterPopoverRef: PdqPopoverDirective;
  @ViewChild('maxxScrollContainer') maxxScrollContainer: ElementRef<HTMLDivElement>;
  activeScrollIndex = 0;
  strategyItems: StrategyItem[] = [];
  strategyItemsRecommendedByMaxx: StrategyItem[] = [];
  filteredStrategyItems: StrategyItem[] = [];
  roadmapItems: StrategyItem[] = [];
  loading = true;
  loadingMaxStrategies = true;
  shouldShowMaxxRecommendations = true;
  expandedIds = new Set<string>();
  expandedMaxxIds = new Set<string>();
  errorMessage: string = '';
  tabs: string[] = [];
  headerTabs: string[] = StrategyHeaderTabs.map((tab) => tab.label);
  currentHeadTab: string = this.headerTabs[0];
  tagsSet: Set<string> = new Set();
  kpiSet: Set<string> = new Set();
  tagsList: OPTION[] = [];
  kpiList: OPTION[] = [];
  filteredTagsList: OPTION[] = [];
  filteredKpiList: OPTION[] = [];
  currentCategory: string = 'All';
  strategySearchControl = new UntypedFormControl();
  tagsSearchControl = new UntypedFormControl();
  kpiSearchControl = new UntypedFormControl();
  activeTags: string[] = [];
  activeKpis: string[] = [];
  headersTexts = headersTexts;
  filterText = 'Filter';
  userName = '';
  private destroyRef = inject(DestroyRef);
  private roadmapReloadSeq = 0;
  readonly StrategyColumns = StrategyColumns;
  readonly optimizeCategoryName = optimizeCategoryName;
  savedStrategyIds = new Set<string>();
  isEssentials = false;

  constructor(
    private readonly strategyCatalogService: StrategyCatalogService,
    private toastService: ToastService,
    private readonly router: Router,
    private route: ActivatedRoute,
    private stateService: StrategyCatalogStateService,
    private strategyOptimizationService: StrategyOptimizationService,
    private shopService: ShopService,
    private userService: UserService,
    private dialog: MatDialog,
    private upgradeToEnterpriseDialog: UpgradeToEnterpriseDialogService,
  ) {}

  openUpgradeDialog(): void {
    this.upgradeToEnterpriseDialog.open();
  }
  ngOnInit() {
    if (this.route.snapshot.url.some((segment) => segment.path === 'roadmap')) {
      this.currentHeadTab = this.headerTabs[1];
    }
    this.shopService.shop$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((shop) => {
      this.isEssentials = !!shop?.isEssentials;
      this.headersTexts = this.isEssentials ? essentialsHeadersTexts : headersTexts;
    });
    this.userService.currentPdqUser$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((user) => {
      this.userName = user?.name?.split(' ')[0] || '';
    });
    if (this.stateService.hasState()) {
      this.activeTags = [...this.stateService.state.activeTags];
      this.activeKpis = [...this.stateService.state.activeKpis];
      this.currentCategory = this.stateService.state.currentCategory;
      this.strategySearchControl.setValue(this.stateService.state.searchValue, { emitEvent: false });
      this.tagsList.forEach((tag) => (tag.active = this.activeTags.includes(tag.value)));
      this.kpiList.forEach((kpi) => (kpi.active = this.activeKpis.includes(kpi.value)));
      this.amountOfActiveFilters();
    }
    this.strategyOptimizationService
      .initialize()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.loadStrategies(),
        error: () => this.loadStrategies(),
      });
    this.strategySearchControl.valueChanges
      .pipe(debounceTime(300), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.filterStrategies();
      });
    this.kpiSearchControl.valueChanges.pipe(debounceTime(300), takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.filterKpiList();
    });
    this.tagsSearchControl.valueChanges.pipe(debounceTime(300), takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.filterTagList();
    });
  }
  ngOnDestroy(): void {
    const url = this.router.url;
    if (url.includes('/strategy-catalog/')) {
      this.stateService.saveState({
        activeTags: this.activeTags,
        activeKpis: this.activeKpis,
        currentCategory: this.currentCategory,
        searchValue: this.strategySearchControl.value || '',
      });
    } else {
      this.stateService.clear();
    }
    this.strategySearchControl.reset();
  }
  loadStrategies(): void {
    forkJoin([
      this.strategyCatalogService.getStrategies(),
      this.strategyCatalogService.getCustomStrategies().pipe(catchError(() => of([]))),
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ([strategies, customStrategies]) => {
          const customItems = customStrategies.map(customStrategyToStrategyItem);
          const allStrategies = [...strategies, ...customItems];
          this.initializeTagsAndKpis(allStrategies);
          this.strategyItems = allStrategies.filter(
            (item) => item[StrategyColumns.PRIMARY_CATEGORY].toLowerCase() !== 'internal validation',
          );
          this.loadRecommendedStrategies(strategies);
          this.loadCategories();
          this.filterStrategies();
          this.loadRoadmapItems();
          this.loading = false;
        },
        error: (error) => {
          this.errorMessage = error?.error?.message || 'Failed to load strategies. Please try again.';
          this.toastService.showToast({
            message: error?.error?.message || 'Failed to load strategies. Please try again.',
            mode: 'danger',
          });
          this.loading = false;
        },
      });
  }
  loadRecommendedStrategies(strategies: StrategyItem[]): void {
    this.loadingMaxStrategies = true;
    this.strategyCatalogService
      .getRecommendedStrategies(this.isEssentials)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (items) => {
          const recommendedIds = items?.ids?.split(',').map((id) => id.trim()) ?? [];
          const topIds = this.isEssentials ? recommendedIds : recommendedIds.slice(0, 3);
          this.strategyItemsRecommendedByMaxx = strategies.filter((item) => topIds.includes(item[StrategyColumns.ID]));
          this.loadingMaxStrategies = false;
        },
        error: () => {
          this.strategyItemsRecommendedByMaxx = [];
          this.loadingMaxStrategies = false;
        },
      });
  }
  loadCategories(): void {
    const categories = new Set(
      this.strategyItems
        .filter((item) => !item.isCustom)
        .map((item) => item[StrategyColumns.PRIMARY_CATEGORY])
        .filter((category) => category.toLowerCase() !== 'internal validation'),
    );
    const hasCustom = this.strategyItems.some((item) => item.isCustom);
    this.tabs = ['All', ...(hasCustom ? ['Custom'] : []), ...categories];
  }
  retryLoad(): void {
    this.loading = true;
    this.loadStrategies();
  }
  filterStrategies(): void {
    let filtered = this.getCategoryFilteredItems();
    filtered = this.applyTagsAndKpisFilter(filtered);
    this.filteredStrategyItems = this.getSearchFilteredItems(filtered);
  }
  getCategoryFilteredItems(): StrategyItem[] {
    const categoryValue = this.currentCategory.trim().toLowerCase();
    if (categoryValue === 'all') return [...this.strategyItems];
    if (categoryValue === 'custom') return this.strategyItems.filter((item) => item.isCustom);
    return this.strategyItems.filter(
      (item) => item[StrategyColumns.PRIMARY_CATEGORY].trim().toLowerCase() === categoryValue,
    );
  }

  filterKpiList(): void {
    this.filteredKpiList = this._filterList(this.kpiList, this.kpiSearchControl.value);
  }

  filterTagList(): void {
    this.filteredTagsList = this._filterList(this.tagsList, this.tagsSearchControl.value);
  }

  private _filterList(list: OPTION[], searchTerm: string): OPTION[] {
    const lowerCaseSearchTerm = searchTerm?.trim().toLowerCase();
    if (!lowerCaseSearchTerm) {
      return list;
    }
    return list.filter((item) => item.value.toLowerCase().includes(lowerCaseSearchTerm));
  }
  getSearchFilteredItems(items: StrategyItem[]): StrategyItem[] {
    const searchValue = this.strategySearchControl.value?.trim().toLowerCase();
    if (searchValue && searchValue.length > 0) {
      return items.filter((item) => item[StrategyColumns.NAME].trim().toLowerCase().includes(searchValue));
    }
    return items;
  }
  applyFilters(): void {
    this.activeTags = this.tagsList.filter((tag) => tag.active).map((tag) => tag.value);
    this.activeKpis = this.kpiList.filter((kpi) => kpi.active).map((kpi) => kpi.value);
    this.filterPopoverRef.close();
    this.filterStrategies();
    this.amountOfActiveFilters();
  }
  clearAllFilters(): void {
    this.activeTags = [];
    this.activeKpis = [];
    this.tagsList.forEach((tag) => (tag.active = false));
    this.kpiList.forEach((kpi) => (kpi.active = false));
    this.filteredTagsList = [...this.tagsList];
    this.filteredKpiList = [...this.kpiList];
    this.kpiSearchControl.setValue('');
    this.tagsSearchControl.setValue('');
    this.amountOfActiveFilters();
  }
  applyTagsAndKpisFilter(items: StrategyItem[]): StrategyItem[] {
    if (this.activeTags.length === 0 && this.activeKpis.length === 0) {
      return items;
    } else if (this.activeTags.length === 0 && this.activeKpis.length > 0) {
      return items.filter((item) => {
        return this.activeKpis.some((kpi) => item.parsedKpiTargets.includes(kpi));
      });
    } else if (this.activeTags.length > 0 && this.activeKpis.length === 0) {
      return items.filter((item) => {
        return this.activeTags.some((tag) => item[StrategyColumns.TAGS].includes(tag));
      });
    } else {
      return items.filter((item) => {
        const matchesTags =
          this.activeTags.length === 0 || this.activeTags.every((tag) => item[StrategyColumns.TAGS].includes(tag));
        const matchesKpis =
          this.activeKpis.length === 0 || this.activeKpis.every((kpi) => item.parsedKpiTargets.includes(kpi));
        return matchesTags && matchesKpis;
      });
    }
  }
  amountOfActiveFilters(): void {
    const totalActiveFilters = this.activeTags.length + this.activeKpis.length;
    this.filterText = totalActiveFilters > 0 ? `Filters (${totalActiveFilters})` : 'Filter';
  }

  setCurrentCategory(category: string): void {
    this.currentCategory = category;
    this.filterStrategies();
  }

  setCurrentHeadTab(tab: string): void {
    this.currentHeadTab = tab;
    const isRoadmap = tab === this.headerTabs[1];
    const path = isRoadmap ? 'roadmap' : '.';
    this.router.navigate([path], {
      relativeTo: this.route.parent,
      replaceUrl: true,
    });
  }

  initializeTagsAndKpis(strategyItems: StrategyItem[]): void {
    strategyItems.forEach((strategyItem) => {
      const tags = strategyItem[StrategyColumns.TAGS].split(', ').map((tag) => tag.trim());
      tags.forEach((tag) => this.tagsSet.add(tag));
      strategyItem.parsedKpiTargets.forEach((kpi) => this.kpiSet.add(kpi));
    });
    this.createFilterOptions();
  }

  createFilterOptions(): void {
    this.kpiList = Array.from(this.kpiSet).map((kpi) => {
      const mappedValue = KpiTargetMapping[kpi] || kpi;
      return {
        label: mappedValue,
        value: mappedValue,
        active: this.activeKpis.includes(mappedValue),
      };
    });
    this.tagsList = Array.from(this.tagsSet)
      .sort()
      .map((tag) => ({
        label: tag,
        value: tag,
        active: this.activeTags.includes(tag),
      }));
    this.filteredKpiList = [...this.kpiList];
    this.filteredTagsList = [...this.tagsList];
  }

  isExpanded(itemId: string, isMaxx = false): boolean {
    if (isMaxx) {
      return this.expandedMaxxIds.has(itemId);
    }
    return this.expandedIds.has(itemId);
  }
  toggleExpanded(itemId: string, isMaxx = false): void {
    if (isMaxx) {
      if (this.isExpanded(itemId, isMaxx)) {
        this.expandedMaxxIds.delete(itemId);
      } else {
        this.expandedMaxxIds.add(itemId);
      }
      return;
    }
    if (this.isExpanded(itemId)) {
      this.expandedIds.delete(itemId);
    } else {
      this.expandedIds.add(itemId);
    }
  }
  shouldShowExpandButton(element: HTMLElement): boolean {
    return element.scrollHeight > element.clientHeight;
  }
  toggleBookmark(event: Event, item: StrategyItem): void {
    event.stopPropagation();
    this.strategyOptimizationService
      .toggleStrategy(item[StrategyColumns.ID], item[StrategyColumns.NAME])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.loadRoadmapItems());
  }

  isBookmarked(item: StrategyItem): boolean {
    return this.savedStrategyIds.has(item[StrategyColumns.ID]);
  }

  onStrategyRemovedFromRoadmap(strategyId: string): void {
    this.strategyOptimizationService
      .unsaveStrategy(strategyId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.loadRoadmapItems());
  }

  openCreateCustomStrategyDialog(): void {
    const dialogRef = this.dialog.open(CreateCustomStrategyDialogComponent, {
      panelClass: 'create-custom-strategy-dialog-panel',
    });
    dialogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if (result) {
          this.loading = true;
          this.loadStrategies();
        }
      });
  }

  navigateToStrategyDetails(item: StrategyItem): void {
    this.router.navigate(['/optimize/strategy-catalog', item[StrategyColumns.ID]]);
  }

  private loadRoadmapItems(): void {
    // Each call gets a monotonic token. Every resolve request is allowed to complete (no HTTP cancellation),
    // but only the newest call's response may write roadmapItems — a late response from a superseded call is
    // dropped, so rapid removals can't overwrite fresh state with a stale snapshot.
    const seq = ++this.roadmapReloadSeq;
    this.savedStrategyIds = this.strategyOptimizationService.getSavedStrategyIds();
    const present = this.strategyItems.filter((item) => this.savedStrategyIds.has(item[StrategyColumns.ID]));
    this.roadmapItems = present;

    const presentIds = new Set(present.map((item) => item[StrategyColumns.ID]));
    const missingIds = [...this.savedStrategyIds].filter((id) => !presentIds.has(id));
    if (missingIds.length === 0) return;

    // Saved strategies no longer in the latest catalog — resolve them all in one request (each from its
    // stored version), so deprecated roadmap items arrive together instead of one round-trip per id.
    this.strategyCatalogService
      .resolveStrategies(missingIds)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((deprecatedItems) => {
        if (seq === this.roadmapReloadSeq) {
          this.roadmapItems = [...present, ...deprecatedItems];
        }
      });
  }

  scrollToCard(index: number): void {
    const container = this.maxxScrollContainer?.nativeElement;
    if (!container) return;
    const cards = container.querySelectorAll('.maxx-card');
    if (cards[index]) {
      cards[index].scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
      this.activeScrollIndex = index;
    }
  }

  onMaxxScroll(): void {
    const container = this.maxxScrollContainer?.nativeElement;
    if (!container) return;
    const cards = container.querySelectorAll('.maxx-card');
    const scrollLeft = container.scrollLeft;
    const cardWidth = cards[0]?.clientWidth || 1;
    const gap = 16;
    this.activeScrollIndex = Math.round(scrollLeft / (cardWidth + gap));
  }
}
